/**
 * Per-flow booking draft persistence — the small half of "persist small, re-derive big"
 * (see BookingFlowResumer.tsx). Only serialisable user input + the shopping-cart id are
 * stored; the authoritative cart and search results are re-fetched on resume.
 *
 * Storage is `sessionStorage`: per-tab, per-session — the same lifetime as `tfw-auth`
 * (src/auth/authStore.ts), and the backend session a cart id is only useful against is
 * itself sessionStorage-scoped. Two tabs therefore keep independent drafts and can't
 * stomp each other's cart.
 *
 * Every function swallows storage failures (disabled storage, quota, corrupt JSON) and
 * degrades to "no draft" — the flow then behaves exactly as it did before this module.
 */

/** Namespaced like the other ad-hoc key in the app (`tfw:sidebarCollapsed`). */
const DRAFT_KEY_PREFIX = 'tfw:booking:';

export const bookingDraftKey = (flowId: string): string => `${DRAFT_KEY_PREFIX}${flowId}`;

export interface BookingDraft<TState> {
  /** Mirrors `BookingFlowPersistConfig.version` — a mismatch discards the draft. */
  v: number;
  stepId: string;
  /** Partialized flow state — see each module's `resume.ts` `partialize`. */
  state: Partial<TState>;
  /** Absolute epoch ms — currently only the air post-search deadline. */
  deadline?: number;
  savedAt: number;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadDraft<TState>(key: string): BookingDraft<TState> | null {
  const s = storage();
  if (!s) return null;
  try {
    const raw = s.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BookingDraft<TState>;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.v !== 'number' || !parsed.stepId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveDraft<TState>(key: string, draft: BookingDraft<TState>): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify(draft));
  } catch {
    // quota / serialisation / disabled — a lost draft is acceptable, a thrown error isn't.
  }
}

export function clearDraft(key: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    // ignore
  }
}

/** Wipe every booking draft — called on logout so a second user on the same tab can't
 *  inherit the first user's carts. Leaves `tfw-auth` / `tfw:sidebarCollapsed` alone. */
export function clearAllBookingDrafts(): void {
  const s = storage();
  if (!s) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const k = s.key(i);
      if (k && k.startsWith(DRAFT_KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => s.removeItem(k));
  } catch {
    // ignore
  }
}
